import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ScrollText } from 'lucide-react'
import {
  AGENT_INSTRUCTION_KIND_LABELS,
  AGENT_INSTRUCTION_KINDS,
  useAgentInstructionsApi,
  type AgentInstructionKind,
  type AgentInstruction_,
} from 'src/api/db/apiAgentInstructions'
import { PageContainer } from 'src/shared/components/PageContainer'
import { EmptyState } from 'src/shared/components/EmptyState'
import {
  FormInput,
  FormSelect,
  FormTextarea,
  FormWrapper,
} from 'src/shared/components/FormComponents'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { Switch } from 'src/shared/components/ui/switch'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/components/ui/utils'

const editSchema = z.object({
  kind: z.enum(AGENT_INSTRUCTION_KINDS),
  title: z.string().min(1),
  body: z.string().min(1),
})

type EditValues = z.infer<typeof editSchema>

export const AdminInstructionsPage = () => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { instructions, isPending, updateInstruction } =
    useAgentInstructionsApi()
  const [editing, setEditing] = useState<AgentInstruction_ | null>(null)

  const methods = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { kind: 'agent', title: '', body: '' },
  })

  const openEdit = (row: AgentInstruction_) => {
    methods.reset({
      kind: row.kind as AgentInstructionKind,
      title: row.title,
      body: row.body,
    })
    setEditing(row)
  }

  const onSave = async (values: EditValues) => {
    if (!editing) return
    await updateInstruction.mutateAsync({
      id: editing.id,
      kind: values.kind,
      title: values.title,
      body: values.body,
    })
    toast({
      variant: 'success',
      title: t('Instruction saved', 'Instruktionen sparad'),
    })
    setEditing(null)
  }

  if (isPending) return <FullScreenLoader />

  return (
    <PageContainer className="gap-8">
      <Column className="gap-6">
        <Row className="items-center gap-2">
          <ScrollText className="h-6 w-6" />
          <Column className="gap-1">
            <h1>{t('Instructions', 'Instruktioner')}</h1>
            <p className="text-sm text-muted-foreground">
              {t(
                'Fine-tune Sara, product selection, and design. Active files are read on every chat turn.',
                'Finjustera Sara, produktval och design. Aktiva filer läses vid varje chatt-svar.'
              )}
            </p>
          </Column>
        </Row>

        {instructions.length === 0 ? (
          <EmptyState
            icon={<ScrollText className="h-5 w-5" />}
            iconColor="blue"
            title={t('No instruction files yet', 'Inga instruktionsfiler än')}
            description={t(
              'Open a chat as superadmin and use Improve this response to add the first file.',
              'Öppna en chatt som superadmin och använd Förbättra svaret för att lägga till den första filen.'
            )}
          />
        ) : (
          AGENT_INSTRUCTION_KINDS.map((kind) => {
            const rows = instructions.filter((row) => row.kind === kind)
            if (rows.length === 0) return null
            return (
              <Column key={kind} className="gap-3">
                <h2 className="text-sm font-medium">
                  {t(
                    AGENT_INSTRUCTION_KIND_LABELS[kind].en,
                    AGENT_INSTRUCTION_KIND_LABELS[kind].sv
                  )}
                </h2>
                {rows.map((row) => (
                  <Row
                    key={row.id}
                    className={cn(
                      'items-start justify-between gap-4 rounded-xl border border-[hsl(var(--border-subtle))] bg-background p-3.5',
                      !row.is_active && 'opacity-60'
                    )}
                  >
                    <Column className="min-w-0 flex-1 gap-1">
                      <Row className="items-center gap-2">
                        <p className="truncate text-sm font-medium">
                          {row.title}
                        </p>
                        {!row.is_active && (
                          <Badge variant="gray" size="sm">
                            {t('Inactive', 'Inaktiv')}
                          </Badge>
                        )}
                      </Row>
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {row.body}
                      </p>
                    </Column>
                    <Row className="shrink-0 items-center gap-3">
                      <Switch
                        size="sm"
                        checked={row.is_active}
                        onCheckedChange={(checked) => {
                          void updateInstruction.mutateAsync({
                            id: row.id,
                            is_active: checked,
                          })
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => openEdit(row)}
                      >
                        {t('Edit', 'Redigera')}
                      </Button>
                    </Row>
                  </Row>
                ))}
              </Column>
            )
          })
        )}
      </Column>

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('Edit instruction', 'Redigera instruktion')}
            </DialogTitle>
          </DialogHeader>
          <FormWrapper methods={methods} onSubmit={onSave}>
            <FormSelect
              name="kind"
              methods={methods}
              label={t('Applies to', 'Gäller')}
              options={AGENT_INSTRUCTION_KINDS.map((kind) => ({
                value: kind,
                label: t(
                  AGENT_INSTRUCTION_KIND_LABELS[kind].en,
                  AGENT_INSTRUCTION_KIND_LABELS[kind].sv
                ),
              }))}
            />
            <FormInput
              name="title"
              methods={methods}
              label={t('Title', 'Titel')}
            />
            <FormTextarea
              name="body"
              methods={methods}
              rows={8}
              label={t('Instruction', 'Instruktion')}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditing(null)}
              >
                {t('Cancel', 'Avbryt')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={updateInstruction.isPending}
              >
                {t('Save', 'Spara')}
              </Button>
            </DialogFooter>
          </FormWrapper>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
